import { Skeleton } from "@/components/_components/skeleton";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import { SUPPLEMENT_TYPES, PAGINATION } from "@/constants/constants";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import toast from "react-hot-toast";
import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, RotateCcw, Eye, BarChart3, Database, ArrowUp, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import CreateSupplementDataDialog from "./_components/dialogs/CreateSupplementDataDialog";
import DeleteSupplementData from "./_components/DeleteSupplementData";
import { usePageHeader } from "@/contexts/page-header-context";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import { Badge } from "@/components/_components/badge";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { ErrorDisplay } from "@/components/_components/error-display";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuthorities } from "@/hooks/useAuthorities";

interface SupplementDataFilter {
  type?: string;
  nameQuery?: string;
}

const SupplementDataPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_MASTER_DATA_VIEW">
      <SupplementDataContent />
    </PageAccessGuard>
  );
};

const SupplementDataContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const { handleError } = useErrorHandler();
  const { hasAuthority } = useAuthorities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    type: searchParams.get("type") || "",
    name: searchParams.get("name") || "",
  });

  const canUpdate = hasAuthority('ADMIN_MASTER_DATA_UPDATE');

  // Default page size should match one of the Select options (10, 20, 50, 100)
  const [pageSize, setPageSize] = useState(10);

  // Use paginated query hook - key it by pageSize to recreate when it changes
  const paginatedQuery = usePaginatedQuery<SupplementDataFilter, any[]>(
    (filter, options) => api.getAllSupplementData(filter, options),
    {
      pageSize: pageSize,
      onError: (error) => {
        handleError(error);
      }
    }
  );

  const {
    data: supplementData,
    isPending: isLoading,
    error,
    executeQuery,
    currentPage,
    totalCount: total,
    setCurrentPage,
  } = paginatedQuery;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = useCallback(() => {
    if (!formData.type) {
      toast.error("Please select a data type");
      return;
    }

    const filter: SupplementDataFilter = {
      type: formData.type,
      ...(formData.name.trim() && { nameQuery: formData.name.trim() }),
    };

    currentFilterRef.current = filter;
    executeQuery(filter, 1); // Reset to first page on new search
    setCurrentPage(1); // Also reset local page state

    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("type", formData.type);
    if (formData.name.trim()) {
      newSearchParams.set("name", formData.name.trim());
    } else {
      newSearchParams.delete("name");
    }
    setSearchParams(newSearchParams);

  }, [formData, executeQuery, searchParams, setSearchParams, setCurrentPage]);

  const handleReset = () => {
    setFormData({
      type: "",
      name: "",
    });

    // Clear URL params
    const newSearchParams = new URLSearchParams();
    setSearchParams(newSearchParams);

    currentFilterRef.current = {};
    setCurrentPage(1);

    toast.success("Form reset successfully");
  };

  // Use a ref to track if we've already loaded initial data
  const initialLoadRef = useRef(false);
  const currentFilterRef = useRef<SupplementDataFilter>({});

  // Initial load with URL parameters
  useEffect(() => {
    if (initialLoadRef.current) {
      return;
    }
    initialLoadRef.current = true;

    if (formData.type) {
      handleSearch();
    }

    // Cleanup function to reset the ref if component unmounts
    return () => {
      initialLoadRef.current = false;
    };
  }, []);

  // Re-query when pageSize changes to ensure correct pagination
  useEffect(() => {
    if (initialLoadRef.current && currentFilterRef.current && Object.keys(currentFilterRef.current).length > 0) {
      // Re-execute query with current filter and reset to page 1 when pageSize changes
      executeQuery(currentFilterRef.current, 1);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]); // Only depend on pageSize

  // Set page header data - only stats, no title
  useEffect(() => {
    setHeaderData({
      stats: supplementData && Array.isArray(supplementData) ? {
        count: total,
        label: 'item',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
      actions: formData.type && canUpdate ? (
        <CreateSupplementDataDialog type={formData.type} />
      ) : undefined,
    });

    // Cleanup: clear header when component unmounts
    return () => {
      setHeaderData(null);
    };
  }, [supplementData, total, formData.type, canUpdate, setHeaderData]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-background">
        {/* Content Skeleton */}
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <div className="space-y-4">
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
            <Card className="shadow-md border-border bg-card">
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <ErrorDisplay
            error={error}
            onRetry={() => {
              if (currentFilterRef.current && Object.keys(currentFilterRef.current).length > 0) {
                executeQuery(currentFilterRef.current, currentPage);
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      {/* Main Content */}
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Search & Filter Section - Modern Clean Design */}
          <div className="bg-card border border-border/50 rounded-lg mb-2">
            {/* Compact Header */}
            <div className="px-4 pt-3 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Select data type and search by name
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Filter Controls */}
            <div className="px-4 pb-0">
              <div className="flex items-end gap-3 flex-wrap">
                {/* Data Type */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="type" className="block text-sm font-semibold text-foreground">
                    Data Type <span className="text-destructive text-base font-bold">*</span>
                  </label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleInputChange("type", value)}
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select data type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPLEMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Name Search */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="name" className="block text-sm font-semibold text-foreground">
                    Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Search by name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isLoading ? (
                      <>
                        <Search className="h-4 w-4 mr-1.5 animate-pulse" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1.5" />
                        Search
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleReset}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {supplementData && (
            <>
              {Array.isArray(supplementData) && supplementData.length === 0 ? (
                <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                        <Search className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Data Found</h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any data matching your search criteria.
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="text-primary border-primary hover:bg-primary/10 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="border border-border/50 bg-card backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                          <Database className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <h2 className="text-xl font-bold text-foreground">
                            {formData.type} Data
                          </h2>
                          <Badge variant="secondary" className="text-xs font-medium">
                            {total} {total === 1 ? 'record' : 'records'}
                          </Badge>
                        </div>
                      </div>

                      <div className="w-full">
                        <div className="overflow-hidden rounded-lg border border-border/50">
                          <Table>
                            <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                              <TableRow>
                                <TableHead className="font-semibold">Sl No</TableHead>
                                <TableHead className="font-semibold">Name</TableHead>
                                <TableHead className="font-semibold">Type</TableHead>
                                <TableHead className="font-semibold">Updated By</TableHead>
                                <TableHead className="font-semibold">Updated At</TableHead>
                                <TableHead className="font-semibold text-center">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {supplementData?.map((row, index) => {
                                // Calculate serial number based on current page and index
                                const serialNumber = (currentPage - 1) * pageSize + index + 1;
                                return (
                                  <TableRow
                                    key={index}
                                    className="transition-colors hover:bg-muted/50 cursor-pointer"
                                  >
                                    <TableCell className="font-medium">{serialNumber}</TableCell>
                                    <TableCell className="font-medium">{row.name || '-'}</TableCell>
                                    <TableCell>{row.type || '-'}</TableCell>
                                    <TableCell>{row.updatedBy || '-'}</TableCell>
                                    <TableCell className="text-sm">{formatDateTime(row.updatedAt)}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-2 justify-center">
                                        {canUpdate && (
                                          <>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors" title="View">
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                            <CreateSupplementDataDialog
                                              supplementData={row}
                                              type={row.type}
                                            />
                                            <DeleteSupplementData id={row.id} />
                                          </>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {/* Pagination matching dashboard style */}
                      <div className="flex items-center justify-between px-4 mt-4">
                        <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                          {total} {total === 1 ? 'record' : 'records'}
                        </div>
                        <div className="flex w-full items-center gap-8 lg:w-fit">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Rows per page</span>
                            <Select
                              value={pageSize.toString()}
                              onValueChange={(value) => {
                                const newSize = Number(value);
                                setPageSize(newSize);
                                setCurrentPage(1); // Reset to first page when page size changes
                              }}
                              disabled={isLoading}
                            >
                              <SelectTrigger className="h-8 w-[70px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                                  <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                              Page {currentPage} of {Math.ceil(total / pageSize) || 1}
                            </span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1 || isLoading}
                                title="First page"
                              >
                                <ChevronsLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1 || isLoading}
                                title="Previous page"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                                title="Next page"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(Math.ceil(total / pageSize))}
                                disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                                title="Last page"
                              >
                                <ChevronsRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}

          {/* No Type Selected */}
          {!formData.type && !supplementData && (
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <CardContent className="p-12">
                <div className="max-w-2xl mx-auto">
                  {/* Icon Section with Gradient Background */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent rounded-2xl flex items-center justify-center backdrop-blur-sm border border-primary/20">
                      <Database className="w-10 h-10 text-primary" />
                    </div>
                    {/* Arrow pointing up */}
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 animate-bounce">
                      <ArrowUp className="w-5 h-5 text-primary/60" />
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Get Started with Master Data</h3>
                    <p className="text-muted-foreground text-base leading-relaxed max-w-md mx-auto">
                      Select a data type from the search filter above to view and manage your master data records.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplementDataPage;